// src/component/AdminOtohikari.js
// OTOHIKARIマップの設定（TARGET SCHUMANN周波数）管理画面
// Firestore: appConfig/otohikari { schumannHz: 7.92 }

import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";

import { doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../api/firebase";
import { AppThemeContext } from "./AppShell";

const DEFAULT_HZ = 7.92;

export default function AdminOtohikari() {
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive || "#5A35FF";
  const surface = theme.surfaceBg || "#FFFFFF";
  const border = theme.bottomBorder || "rgba(0,0,0,0.08)";
  const textBase = theme.railText || "rgba(40,40,60,0.9)";

  const [loading, setLoading] = useState(true);
  const [remoteHz, setRemoteHz] = useState(DEFAULT_HZ);
  const [hz, setHz] = useState(String(DEFAULT_HZ));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "appConfig", "otohikari"),
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        const v = typeof data.schumannHz === "number" ? data.schumannHz : DEFAULT_HZ;
        setRemoteHz(v);
        setHz(String(v));
        setDirty(false);
        setLoading(false);
      },
      (err) => {
        console.error("[AdminOtohikari] onSnapshot error:", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const handleHzChange = (e) => {
    setHz(e.target.value);
    setDirty(true);
    setSaveMsg("");
  };

  const parsedHz = Number(hz);
  const valid = Number.isFinite(parsedHz) && parsedHz > 0 && parsedHz < 1000;

  const handleSave = async () => {
    if (!valid) return;
    setSaving(true);
    setSaveMsg("");
    try {
      await setDoc(doc(db, "appConfig", "otohikari"), {
        schumannHz: parsedHz,
        updatedAt: serverTimestamp(),
      });
      setSaveMsg("保存しました");
      setDirty(false);
    } catch (err) {
      console.error("[AdminOtohikari] save error:", err);
      setSaveMsg("保存に失敗しました: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2.5, sm: 3.5 }, maxWidth: 520 }}>
      <Typography
        variant="overline"
        sx={{ letterSpacing: "0.18em", fontSize: 11, opacity: 0.8, color: textBase }}
      >
        ADMIN
      </Typography>
      <Typography
        variant="h5"
        sx={{ fontWeight: 800, letterSpacing: "0.03em", color: textBase, mb: 0.5 }}
      >
        OTOHIKARIマップ設定
      </Typography>
      <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.6)", mb: 3 }}>
        トップページのOTOHIKARIマップに表示される TARGET SCHUMANN 周波数（Hz）を設定します。
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 2, color: textBase, fontWeight: 700 }}>
          TARGET SCHUMANN (Hz)
        </Typography>

        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            label="周波数"
            type="number"
            size="small"
            value={hz}
            onChange={handleHzChange}
            inputProps={{ min: 0, step: 0.01 }}
            sx={{ width: 160 }}
          />
          <Typography variant="h6" sx={{ color: textBase }}>Hz</Typography>
        </Stack>

        <Box
          sx={{
            mt: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: `${accent}08`,
            border: `1px solid ${accent}22`,
          }}
        >
          <Typography variant="body2" sx={{ color: textBase }}>
            現在の設定: <strong>{remoteHz} Hz</strong>
          </Typography>
          {dirty && valid && (
            <Typography variant="body2" sx={{ color: accent, mt: 0.5 }}>
              変更後: <strong>{parsedHz} Hz</strong>
            </Typography>
          )}
          {dirty && !valid && (
            <Typography variant="body2" sx={{ color: "error.main", mt: 0.5 }}>
              0より大きい数値を入力してください
            </Typography>
          )}
        </Box>

        <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: "rgba(0,0,0,0.45)" }}>
          例: 7.92 / 7.95 など。小数2桁程度で入力します。
        </Typography>

        <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
            disabled={!dirty || !valid || saving}
            onClick={handleSave}
            sx={{
              textTransform: "none",
              bgcolor: accent,
              "&:hover": { bgcolor: accent, filter: "brightness(0.92)" },
            }}
          >
            保存
          </Button>
        </Stack>

        {saveMsg && (
          <Alert
            severity={saveMsg.startsWith("保存しました") ? "success" : "error"}
            sx={{ mt: 2 }}
          >
            {saveMsg}
          </Alert>
        )}
      </Paper>
    </Box>
  );
}
